import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiPlanLiabilityModal({ onAcknowledged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/training/ai/acknowledged`);
        if (!res.data?.acknowledged) {
          setOpen(true);
        } else {
          onAcknowledged?.();
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [onAcknowledged]);

  const handleAccept = async () => {
    if (!agreed) return;
    setSaving(true);
    try {
      await axios.post(`${API}/training/ai/acknowledge`);
      setOpen(false);
      onAcknowledged?.();
    } catch (e) {
      toast.error('Failed to record acknowledgement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg" data-testid="ai-plan-liability-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600" />Before you start</DialogTitle>
          <DialogDescription className="pt-2">
            The AI Training Plan Generator suggests drills and structures based on your inputs. <strong>It is not a substitute for qualified coaching judgement.</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-700">
          <ul className="list-disc list-inside space-y-1.5">
            <li>Always review every drill before delivering it to your players.</li>
            <li>Adapt for the age, ability and physical condition of your group.</li>
            <li>Conduct your own safety check of the pitch and equipment.</li>
            <li>Follow your governing body's safeguarding and contact-rule guidance.</li>
            <li>Use your own coaching qualifications and first-aid training.</li>
          </ul>
          <label className="flex items-start gap-2 mt-4 cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={setAgreed} data-testid="ai-plan-liability-checkbox" />
            <span className="text-sm">
              I understand AI suggestions need coaching adaptation for safety, and I accept full responsibility for the sessions I deliver.
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            onClick={handleAccept}
            disabled={!agreed || saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ai-plan-liability-accept-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "I agree, let's get started"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
